import { IFileDto } from '../../../config/fileConfig'
import * as React from 'react'
import { FunctionComponent, PropsWithChildren, ReactElement, useEffect, useState } from 'react'
import { TaskMutationDto, TaskStatus } from '../../../config/taskConfig'
import { useSelector } from 'react-redux'
import { Store } from '../../../reducers'
import { Destination } from '../../../context/AxiosContext'
import { Formik, useFormikContext } from 'formik'
import Form, { EntityFormProps } from '../Form'
import { MessageBar, MessageBarType, Stack } from '@fluentui/react'
import LegacyUserDropdown from '../../Dropdown/LegacyUserDropdown'
import FileDropdown from '../../Dropdown/File/FileDropdown'
import DocumentDropdown from '../../Dropdown/DocumentDropdown'
import { getDropdownProps } from '../../../helpers/formHelper'
import * as Yup from 'yup'
import { useLocation } from 'react-router-dom'
import LegacyLocalDatePicker from '../../../components/LegacyLocalDatePicker'
import { formatIsoDate } from '../../../helpers/dateHelper'
import FormikTextField from '../../../components/Formik/TextField/FormikTextField'
import FormikDropdown from '../../../components/Formik/Dropdown/FormikDropdown'
import Loading from '../../../components/Loading'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import useUser from '../../../hooks/useUser'
import useAxios from '../../../hooks/useAxios'
import useFileDocuments from '../../../hooks/file/useFileDocuments'
import useRoles, { rolesWriteAllTask } from '../../../hooks/useRoles'
import FormContainer from '../../../components/Form/FormContainer'
import { useTranslation } from 'react-i18next'
import { TaskStatusLabels } from '../../TagPicker/TaskStatusPicker'

type Props = EntityFormProps<TaskMutationDto, TaskMutationDto>

const validationSchema = Yup.object({
    description: Yup.string().required(),
    due: Yup.string().required(),
    assigneeId: Yup.string().uuid().required(),
    assignerId: Yup.string().uuid().required(),
    fileId: Yup.string().uuid().required(),
    attachmentIds: Yup.array(),
    status: Yup.string().required(),
})

const Container: FunctionComponent<{ useFileContext: boolean }> = props => {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const { initialValues, errors, values, setFieldValue } = useFormikContext<TaskMutationDto>()
    const context = useFormikContext<TaskMutationDto>()
    const currentFile = useSelector<Store, IFileDto>(state => state.files.current.data)
    const [selectedFile, setSelectedFile] = useState<IFileDto>(currentFile)
    // Fetch a new list of documents when the file is changed.
    const documents = useFileDocuments(selectedFile?.reference ?? undefined)

    const roles = useRoles()

    useEffect(() => {
        // This is not the initial state.
        if (!values.fileId || values.fileId !== selectedFile?.id) {
            setFieldValue('attachmentIds', initialValues.fileId === values.fileId ? initialValues.attachmentIds : [])

            if (!values.fileId) {
                setSelectedFile(undefined)
            } else if (values.fileId === currentFile?.id) {
                setSelectedFile(currentFile)
            } else {
                axios.get<IFileDto>(`/file/${values.fileId}`).then(result => setSelectedFile(result.data))
            }
        }
    }, [values.fileId])

    return (
        <Stack tokens={{ childrenGap: 's1' }}>
            <FormikTextField
                context={context}
                label={translate('task.description')}
                fieldName="description"
                textFieldProps={{
                    required: true,
                    multiline: true,
                    rows: 5,
                    autoFocus: true,
                    autoAdjustHeight: true,
                }}
            />
            <LegacyLocalDatePicker
                value={values.due}
                onSelectDate={date => setFieldValue('due', date)}
                datePickerProps={{
                    label: translate('task.date'),
                    minDate: new Date(),
                }}
            />
            <LegacyUserDropdown
                label={translate('task.assigneeid')}
                {...getDropdownProps(values.assigneeId, errors?.assigneeId, value =>
                    setFieldValue('assigneeId', value),
                )}
                tabIndex={-1}
            />
            <LegacyUserDropdown
                label={translate('task.assignerid')}
                readOnly={!roles.any(rolesWriteAllTask)}
                {...getDropdownProps(values.assignerId, errors?.assignerId, value =>
                    setFieldValue('assignerId', value),
                )}
                tabIndex={-1}
            />
            <FileDropdown
                initialValues={[values.fileId]}
                {...getDropdownProps(values.fileId, errors?.fileId, value => setFieldValue('fileId', value))}
                tabIndex={props.useFileContext ? -1 : undefined}
                readOnly={props.useFileContext}
                disabled={props.useFileContext}
            />
            {selectedFile && (
                <DocumentDropdown
                    label={translate('task.attachmentids')}
                    {...getDropdownProps(
                        values.attachmentIds,
                        errors?.attachmentIds,
                        value => setFieldValue('attachmentIds', value),
                        true,
                    )}
                    data={documents.data}
                    required={false}
                    tabIndex={-1}
                />
            )}
            <FormikDropdown
                context={context}
                label={translate('task.status.label')}
                fieldName="status"
                options={[...TaskStatusLabels.entries()].map(([key, value]) => ({
                    key: key,
                    text: translate(value.name),
                }))}
                required={true}
                tabIndex={-1}
            />
        </Stack>
    )
}

export default function TaskForm(props: PropsWithChildren<Props>): ReactElement {
    const axios = useAxios()
    const currentUser = useUser()
    const { t: translate } = useTranslation()
    const currentFile = useSelector<Store, IFileDto>(state => state.files.current.data)
    const location = useLocation()
    const [initialAttachmentIds, setInitialAttachmentIds] = useState<string[]>()
    const numRemovedAttachments = (props.entity?.data.attachmentIds || []).length - (initialAttachmentIds || []).length

    useEffect(() => {
        Promise.all(
            (props.entity?.data.attachmentIds || []).map(attachmentId =>
                axios
                    .get<DriveItem>(`/items/${attachmentId}`, {
                        baseURL: Destination.Documents,
                        headers: { SuppressErrors: 'true' },
                        params: { $select: 'id' },
                    })
                    .then(() => attachmentId)
                    .catch(() => undefined),
            ),
        ).then(attachmentIds => setInitialAttachmentIds(attachmentIds.filter(attachmentId => attachmentId)))
    }, [props.entity?.data.attachmentIds])

    if (!Array.isArray(initialAttachmentIds)) {
        return (
            <Loading
                label={translate('task.edit.form.attachments.loading', {
                    count: props.entity?.data.attachmentIds.length || 1,
                })}
            />
        )
    }

    return (
        <Formik<TaskMutationDto>
            initialValues={{
                ...(props.entity?.data || {
                    status: TaskStatus.Open,
                    description: '',
                    due: formatIsoDate(new Date()),
                    assigneeId: currentUser?.id,
                    assignerId: currentUser?.id,
                    fileId: props.useFileContext ? currentFile?.id : undefined,
                }),
                attachmentIds: initialAttachmentIds,
                // Add any props that have been passed in the current request.
                ...(location.state as Record<string, unknown>),
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={props.onSubmit}
        >
            <Form>
                {numRemovedAttachments > 0 && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        {translate('task.edit.form.attachments.removed', { count: numRemovedAttachments })}
                    </MessageBar>
                )}
                <FormContainer>
                    <Container useFileContext={props.useFileContext} />
                    {props.children}
                </FormContainer>
            </Form>
        </Formik>
    )
}
